import React from 'react';
import axios from 'axios';
import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { responseHandler } from '../components/utility/Utility';
import '../assets/css/views/settings.css';
import SettingsTeam from '../components/SettingsTeam';

export default function Settings() {
    const navigate = useNavigate();
    const { userProfile, getJwtToken, handleSignOut, canWrite } = useContext(AuthContext);
    const [formValues, setFormValues] = useState({
        name: "",
        timezone: ""
    });
    const [errors, setErrors] = useState({
        name: null,
        timezone: null
    });
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const handleLogout = async () => {
        await handleSignOut();
        // Navigate to login without any state, clearing redirect history
        navigate('/login', { replace: true });
    };

    const plans = [
        { name: 'Free', price: 0, orders: 'Up to 100' },
        { name: 'Starter', price: 19.99, orders: 'Up to 500', overcharge: '0.15' },
        { name: 'Grow', price: 49.99, orders: 'Up to 2,500', overcharge: '0.10' },
        { name: 'Enterprise', price: 99.99, orders: 'Unlimited', overcharge: null }
    ];

    const getTrialDaysRemaining = () => {
        if (userProfile?.store?.billing?.trialEndsAt) {
            const remainingTime = new Date(userProfile.store.billing.trialEndsAt).getTime() - Date.now();
            if (remainingTime > 0) {
                return Math.ceil(remainingTime / (1000 * 60 * 60 * 24));
            }
        }
        return null;
    };

    const trialDaysRemaining = getTrialDaysRemaining();

    useEffect(() => {
        const getSettings = async () => {
            setSettingsLoading(true);
            try {
                const token = await getJwtToken();
                const res = await axios.get('https://api.amplifyloyalty.com/app/stores/settings',
                {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.name === 'BadRequestError') {
                    handleSignOut();
                } else {
                    setFormValues({...res.data});
                }
            } catch (err) {
                console.log(err);
            } finally {
                setSettingsLoading(false);
            }
        }
        getSettings();
    }, [])

    const selectPlan = async (plan) => {
        try {
            const token = await getJwtToken();
            if (plan.name === 'Free') {
                // Handle selecting the free plan
                await axios.put(
                    'https://api.amplifyloyalty.com/app/billing/plan',
                    { plan: plan.name.toLowerCase() },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // Reload to reflect the change in plan status
                window.location.reload();
            } else {
                // Handle selecting a paid plan
                const res = await axios.post(
                    'https://api.amplifyloyalty.com/app/billing/subscribe',
                    { name: plan.name, price: plan.price },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.data) {
                    // Fire Facebook Pixel Purchase event
                    if (window.fbq) {
                        window.fbq('track', 'Purchase', {
                            value: plan.price,
                            currency: 'USD'
                        });
                    }
                    // Redirect to Shopify's confirmation URL
                    window.location.href = res.data;
                }
            }
        } catch (err) {
            responseHandler(err, "error", "Could not initiate subscription. Please try again later.");
            console.error(err);
        }
    };

    const timezones = [
        { value: 'Pacific/Midway', label: 'UTC-11:00 - Pacific/Midway' },
        { value: 'America/Adak', label: 'UTC-10:00 - America/Adak' },
        { value: 'Pacific/Marquesas', label: 'UTC-09:30 - Pacific/Marquesas' },
        { value: 'America/Anchorage', label: 'UTC-09:00 - America/Anchorage' },
        { value: 'America/Los_Angeles', label: 'UTC-08:00 - America/Los Angeles (PST)' },
        { value: 'America/Denver', label: 'UTC-07:00 - America/Denver (MST)' },
        { value: 'America/Chicago', label: 'UTC-06:00 - America/Chicago (CST)' },
        { value: 'America/New_York', label: 'UTC-05:00 - America/New York (EST)' },
        { value: 'America/Caracas', label: 'UTC-04:00 - America/Caracas' },
        { value: 'America/Sao_Paulo', label: 'UTC-03:00 - America/Sao Paulo' },
        { value: 'Atlantic/South_Georgia', label: 'UTC-02:00 - Atlantic/South Georgia' },
        { value: 'Atlantic/Azores', label: 'UTC-01:00 - Atlantic/Azores' },
        { value: 'Europe/London', label: 'UTC+00:00 - Europe/London (GMT)' },
        { value: 'Europe/Berlin', label: 'UTC+01:00 - Europe/Berlin (CET)' },
        { value: 'Europe/Athens', label: 'UTC+02:00 - Europe/Athens (EET)' },
        { value: 'Asia/Baghdad', label: 'UTC+03:00 - Asia/Baghdad' },
        { value: 'Asia/Tehran', label: 'UTC+03:30 - Asia/Tehran' },
        { value: 'Asia/Dubai', label: 'UTC+04:00 - Asia/Dubai' },
        { value: 'Asia/Kabul', label: 'UTC+04:30 - Asia/Kabul' },
        { value: 'Asia/Karachi', label: 'UTC+05:00 - Asia/Karachi' },
        { value: 'Asia/Kolkata', label: 'UTC+05:30 - Asia/Kolkata' },
        { value: 'Asia/Kathmandu', label: 'UTC+05:45 - Asia/Kathmandu' },
        { value: 'Asia/Dhaka', label: 'UTC+06:00 - Asia/Dhaka' },
        { value: 'Asia/Yangon', label: 'UTC+06:30 - Asia/Yangon' },
        { value: 'Asia/Bangkok', label: 'UTC+07:00 - Asia/Bangkok' },
        { value: 'Asia/Shanghai', label: 'UTC+08:00 - Asia/Shanghai' },
        { value: 'Asia/Tokyo', label: 'UTC+09:00 - Asia/Tokyo' },
        { value: 'Australia/Darwin', label: 'UTC+09:30 - Australia/Darwin' },
        { value: 'Australia/Sydney', label: 'UTC+10:00 - Australia/Sydney' },
        { value: 'Pacific/Norfolk', label: 'UTC+11:00 - Pacific/Norfolk' },
        { value: 'Pacific/Auckland', label: 'UTC+12:00 - Pacific/Auckland' },
        { value: 'Pacific/Chatham', label: 'UTC+12:45 - Pacific/Chatham' },
        { value: 'Pacific/Tongatapu', label: 'UTC+13:00 - Pacific/Tongatapu' },
        { value: 'Pacific/Kiritimati', label: 'UTC+14:00 - Pacific/Kiritimati' },
    ];

    const checkErrors = async () => {
        let errors = false;
        for (let field of Object.keys(formValues)) {
            switch (field) {
                case "name":
                    const nameRegxp = /([A-Za-z0-9]+)/g
                    if (!formValues.name.match(nameRegxp)) {
                        setErrors({...errors, name: 'Must include at least 1 number or letter.'})
                        errors = true;
                    };
                    break;
                default:
            }
        }
        return errors;
    }

    const submit = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!canWrite) return;
        setErrors({
            name: null,
            timezone: null
        });
        setSaving(true);
        const isErrors = await checkErrors();
        if (!isErrors) {
            try {
                const token = await getJwtToken();
                const res = await axios.put('https://api.amplifyloyalty.com/app/stores/settings',
                    {...formValues},
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                if (res.data.name === 'BadRequestError') {
                    handleSignOut();
                } else {
                    responseHandler(res, "success", "Settings Saved!");
                }
            } catch (err) {
                responseHandler(err, "error", "Error: try again later.")
                console.log(err);
            } finally {
                setSaving(false);
            }
        } else {
            setSaving(false);
        }
    }

    const fieldsDisabled = settingsLoading || saving || !canWrite;
    
      
    return(
        <div className="main">
            <div className="settings">
                <div className="settings-header">
                    <h2>General Settings</h2>
                    <button onClick={handleLogout} className="settings__logout-btn">Logout</button>
                </div>
                {!canWrite && (
                    <p className="settings__sub-heading" style={{ marginBottom: '1rem' }}>
                        You have read-only access. Contact your store owner to change settings.
                    </p>
                )}
                <form onSubmit={submit}>
                    <div className="settings__container">
                        {/* <div className="settings__option">
                            <div>Include tax in order totals.</div>
                            <button className="gradient">Toggle</button>
                        </div>
                        <div className="settings__option">
                            <div>Automatically subtract points on refund.</div>
                            <button className="gradient">Toggle</button>
                        </div> */}
                        <div className="settings__option">
                            <label htmlFor="name" className="settings__option-label">Store name</label>
                            <input name="name" id="name" className="settings__option-input" value={formValues.name} onChange={(e) => setFormValues({...formValues, name: e.target.value})} disabled={fieldsDisabled}/>
                            {errors.name && <div className="settings__option-error">{errors.name}</div>}
                        </div>
                        <div className="settings__option">
                            <label htmlFor="timezone" className="settings__option-label">Timezone</label>
                            <select name="timezone" className="settings__option-input settings__option-input--select" value={formValues.timezone} onChange={(e) => setFormValues({...formValues, timezone: e.target.value})} disabled={fieldsDisabled}>
                                {timezones.map(tmzn => <option key={tmzn.value} value={tmzn.value}>{tmzn.label}</option>)}
                            </select>
                            {errors.timezone && <div className="settings__option-error">{errors.timezone}</div>}
                        </div>
                        {canWrite && (
                          <div className="settings__button-container">
                              <button type="submit" className="settings__save gradient btn" disabled={fieldsDisabled}>Save</button>
                          </div>
                        )}
                    </div>
                </form>

                <hr className="signup__horizontal-rule" style={{ margin: '2rem 0' }} />
                <SettingsTeam />

                {/* BILLING SECTION - TEMPORARILY DISABLED FOR FREE UNLIMITED ACCESS
                    Uncomment this section when ready to re-enable paid plans */}
                {false && (
                    <>
                        <div className="settings__billing-header">
                            <h3 className="settings__heading">Billing</h3>
                            <p className="settings__sub-heading">
                                {trialDaysRemaining !== null
                                    ? `You have ${trialDaysRemaining} days remaining in your trial.`
                                    : "To update your plan, select a new plan below."}
                            </p>
                        </div>
                        <div className="settings__plans-container">
                            <div className="settings__plans">
                                {plans.map(plan => {
                                    const isCurrentPlan = userProfile?.store?.billing?.plan === plan.name.toLowerCase();
                                    return (
                                        <div key={plan.name} className={`settings__plan ${isCurrentPlan ? 'settings__plan--current' : ''}`}>
                                            {isCurrentPlan && <div className="settings__plan-current-badge">Current</div>}
                                            <div className="settings__plan-header">
                                                <h4 className="settings__plan-name">{plan.name}</h4>
                                                <div className="settings__plan-price">
                                                    ${plan.price}
                                                    <span>/mo</span>
                                                </div>
                                            </div>
                                            <ul className="settings__plan-features">
                                                <li className="settings__plan-feature">
                                                    <i className="fa-solid fa-check settings__plan-icon"></i> {plan.orders} Orders
                                                </li>
                                                {plan.overcharge && (
                                                    <li className="settings__plan-feature">
                                                        <i className="fa-solid fa-check settings__plan-icon"></i> ${plan.overcharge} per extra order
                                                    </li>
                                                )}
                                                <li className="settings__plan-feature">
                                                    <i className="fa-solid fa-check settings__plan-icon"></i> All Features Included
                                                </li>
                                            </ul>
                                            <div className="settings__plan-button-container">
                                                <button 
                                                    className="settings__plan-button gradient btn"
                                                    onClick={() => selectPlan(plan)}
                                                    disabled={isCurrentPlan}
                                                >
                                                    {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                                                </button>
                                            </div>
                                        </div>
                                    )})}
                            </div>
                        </div>

                        <div className="settings__cancellation-notice">
                            <p>
                                Want to cancel your subscription? Simply <a 
                                    href={`https://admin.shopify.com/store/${userProfile?.store?.store.replace('.myshopify.com', '')}/settings/apps/app_installations/app/amplify-loyalty-program`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                >
                                    uninstall the app
                                </a> from your Shopify store.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}